import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Sparkles, Headphones, Rocket, ArrowRight, Check, LogIn, UserPlus,
  AlertTriangle, Building2, User, Users, Network, MailCheck, Briefcase,
  Stethoscope, Eye, Ear, MessageCircle, HeartPulse, Calendar, Phone,
  TrendingUp, Lock, FileCheck, RefreshCw, Star, MailPlus, ArrowDownRight,
  UserRound, ShieldAlert, Briefcase as BriefcaseIcon,
} from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import MssanteDomainCheck from "../components/MssanteDomainCheck";
import MssanteIndividualVsStructure from "../components/MssanteIndividualVsStructure";
import PersonaCardsGrid from "../components/PersonaCardsGrid";

// ============================================================
// SEO — page cible « MSSanté opticien » + structures paramédicales
// ============================================================

const FAQ = [
  {
    q: "Qu'est-ce que la MSSanté ?",
    a: "La MSSanté (Messagerie Sécurisée de Santé) désigne l'ensemble des messageries électroniques réservées aux professionnels habilités des secteurs sanitaire, médico-social et social pour échanger des données de santé de façon sécurisée. Toutes ces messageries appartiennent à un même Espace de Confiance piloté par l'Agence du Numérique en Santé (ANS) : un professionnel équipé chez un opérateur peut échanger avec un professionnel équipé chez un autre opérateur.",
  },
  {
    q: "Qu'est-ce que l'Espace de Confiance MSSanté ?",
    a: "C'est le cadre commun défini par l'ANS auquel adhèrent les opérateurs de messagerie MSSanté (établissements, éditeurs, opérateurs de services). Il repose sur des exigences de sécurité partagées et sur l'annuaire national des adresses MSSanté, ce qui rend les messageries interopérables entre elles. La messagerie MetaConnect est opérée dans l'Espace de Confiance MSSanté de l'ANS.",
  },
  {
    q: "MSSanté pro : quelle adresse pour un professionnel de santé ?",
    a: "Deux types d'adresses coexistent : la boîte personnelle, nominative et rattachée à un seul professionnel (par exemple sous le domaine @pro.mssante.fr), et la boîte organisationnelle, rattachée à une structure (cabinet, magasin, centre) et partageable entre plusieurs utilisateurs habilités. Le bon choix dépend de votre organisation : les pages par métier détaillent les usages pour les médecins, infirmiers libéraux, opticiens et audioprothésistes.",
  },
  {
    q: "Quelle est la différence entre une boîte MSSanté individuelle et organisationnelle ?",
    a: "Une boîte MSSanté individuelle (ex. jean.dupond@pro.mssante.fr) est rattachée à une seule personne physique et utilise obligatoirement le domaine partagé @pro.mssante.fr. Si elle est absente ou quitte la structure, les échanges avec les prescripteurs sont interrompus. Une boîte organisationnelle (ex. cabinet-lefevre@opticiens.mssante.fr) est rattachée à votre structure (SIRET ou FINESS), partagée par plusieurs utilisateurs avec des droits différenciés (responsable, cotitulaire, délégataire), survit aux mouvements RH et bénéficie d'un sous-domaine métier exclusif à MetaConnect (@opticiens.mssante.fr, @optique.mssante.fr, @ohmyvue.mssante.fr). C'est le modèle recommandé par l'Agence du Numérique en Santé pour les opticiens, audioprothésistes et tous les paramédicaux exerçant en équipe.",
  },
  {
    q: "Un opticien peut-il avoir une messagerie MSSanté ?",
    a: "Oui. Depuis l'élargissement du référentiel MSSanté de l'ANS (2022-2024), les opticiens-lunetiers diplômés disposant d'un numéro RPPS ou ADELI peuvent créer une boîte MSSanté pour échanger avec les ophtalmologistes prescripteurs, les orthoptistes et les autres acteurs du parcours visuel. La messagerie organisationnelle MetaConnect est particulièrement adaptée aux cabinets, magasins et chaînes d'optique exerçant en équipe.",
  },
  {
    q: "Combien de temps pour créer ma messagerie MSSanté avec MetaConnect ?",
    a: "La création de votre espace pro et le paramétrage de votre boîte MSSanté prennent moins de 10 minutes. Les identifiants techniques de connexion vous sont envoyés par email sous 24 à 48 heures ouvrées après vérification réglementaire. Vous recevez également un PDF récapitulatif détaillé (structure, responsable, accès) pour votre registre RGPD.",
  },
  {
    q: "Combien d'utilisateurs peuvent accéder à une boîte organisationnelle ?",
    a: "Il n'y a pas de limite stricte. Vous pouvez désigner 1 responsable principal, autant de cotitulaires (pleine utilisation de la boîte) que nécessaire et autant de délégataires (lecture/délégation) que vous souhaitez. Les droits sont modifiables instantanément depuis votre espace pro.",
  },
  {
    q: "Qu'est-ce qu'un cotitulaire et un délégataire d'une boîte MSSanté ?",
    a: "Le responsable est le porteur juridique de la boîte. Les cotitulaires sont des titulaires secondaires qui peuvent envoyer, recevoir et archiver des messages au même titre que le responsable. Les délégataires ont un accès en lecture/délégation, typiquement utilisé pour les assistants ou secrétaires qui préparent les envois sans engager la responsabilité de la structure. Cette granularité permet de respecter le secret médical tout en mutualisant le travail.",
  },
  {
    q: "Mes messages sont-ils chiffrés et conformes RGPD ?",
    a: "Oui. La messagerie MSSanté repose sur le standard S/MIME, l'hébergement est certifié HDS (Hébergeur de Données de Santé), et l'accès est tracé conformément aux exigences de la PSSI-S. MetaConnect fournit en complément un récapitulatif PDF de chaque création de boîte pour alimenter votre registre de traitement RGPD.",
  },
  {
    q: "Puis-je changer mon nom de domaine MSSanté ou conserver mon adresse ?",
    a: "Votre adresse organisationnelle (ex. contact@votrestructure.opticiens.mssante.fr) reste stable dans le temps, indépendamment des changements de personnel. C'est l'un des bénéfices clés de la boîte organisationnelle vs. individuelle : les prescripteurs continuent de vous joindre à la même adresse pendant des années.",
  },
  {
    q: "Quels sous-domaines MSSanté MetaConnect met à disposition ?",
    a: "MetaConnect est propriétaire de plusieurs sous-domaines MSSanté métier réservés aux structures clientes : @opticiens.mssante.fr, @optique.mssante.fr et @ohmyvue.mssante.fr. Ces sous-domaines vous permettent d'avoir une adresse à votre image (ex. cabinet-lefevre@opticiens.mssante.fr) au lieu du domaine standard @pro.mssante.fr partagé par tous. C'est un avantage exclusif : seuls les opérateurs MSSanté agréés peuvent acquérir et exploiter ces sous-domaines auprès de l'Agence du Numérique en Santé.",
  },
  {
    q: "Peut-on avoir un sous-domaine MSSanté à son nom d'enseigne ou de groupe ?",
    a: "Oui. En tant qu'opérateur MSSanté agréé, MetaConnect peut réserver un sous-domaine personnalisé pour votre enseigne, votre groupe ou votre réseau (ex. paris@votre-enseigne.mssante.fr, lyon@votre-enseigne.mssante.fr). Cette prestation fait l'objet d'un devis spécifique : elle inclut la réservation auprès de l'ANS, le paramétrage technique et le déploiement multi-sites. Comptez 2 à 4 semaines entre la commande et la mise en service. Idéal pour les chaînes d'optique, audioprothèse, centres de santé et groupes médico-sociaux qui veulent être identifiés à l'échelle nationale.",
  },
];

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Messagerie MSSanté organisationnelle pour opticiens et paramédicaux",
  serviceType: "MSSanté",
  provider: {
    "@type": "Organization",
    name: "MetaConnect",
    url: "https://metaconnect.fr",
    logo: "https://metaconnect.fr/og-image.jpg",
  },
  areaServed: { "@type": "Country", name: "France" },
  audience: {
    "@type": "BusinessAudience",
    audienceType: "Opticiens-lunetiers, audioprothésistes, orthophonistes, orthoptistes, centres de santé, structures médico-sociales",
  },
  description:
    "Création et paramétrage en moins de 10 minutes d'une boîte mail organisationnelle MSSanté pour opticiens, audioprothésistes et professions paramédicales. Multi-utilisateurs (responsable, cotitulaires, délégataires), conforme ANS, hébergement HDS, échanges sécurisés avec les prescripteurs.",
  offers: {
    "@type": "Offer",
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
    url: "https://metaconnect.fr/mssante/inscription",
  },
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const MSSANTE_SCHEMA = [
  breadcrumb([{ name: "Accueil", url: "/" }, { name: "MSSanté opticien", url: "/mssante" }]),
  SERVICE_SCHEMA,
  FAQ_SCHEMA,
];

// ============================================================
// Page
// ============================================================

export default function Mssante() {
  return (
    <div data-testid="page-mssante" style={{ background: "#F8FAFC" }}>
      <SEO
        title="MSSanté pro : messagerie sécurisée partagée"
        description="La messagerie sécurisée MSSanté pour tous les professionnels de santé : boîte organisationnelle multi-utilisateurs, conforme ANS, hébergée HDS en France."
        canonical="/mssante"
        schema={MSSANTE_SCHEMA}
      />

      <Hero />
      <MssanteDomainCheck />
      <PersonaCardsGrid />
      <ProblemSection />
      <SolutionSection />
      <ComparisonSection />
      <MssanteIndividualVsStructure />
      <ContinuitySection />
      <BrandImageSection />
      <ParamedicalSection />
      <OhMyVueIntegrationSection />
      <NationalAlignmentSection />
      <PlatformSection />
      <BenefitsSection />
      <FAQSection />
      <SisterPagesSection />
      <FinalCTA />
    </div>
  );
}

// ============================================================
// Sections
// ============================================================

function Hero() {
  return (
    <section className="relative pt-16" aria-labelledby="hero-h1">
      <div className="grid lg:grid-cols-2 min-h-[520px]">
        {/* LEFT — Navy */}
        <div
          className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center"
          style={{ background: "#0F2B5B" }}
        >
          <div
            className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
            style={{
              background: "rgba(79,195,247,0.12)",
              border: "1px solid rgba(79,195,247,0.30)",
              color: "#4FC3F7",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            <ShieldCheck size={13} /> MSSanté · Médical & paramédical
          </div>
          <h1
            id="hero-h1"
            className="mt-6 font-display"
            style={{
              color: "#ffffff",
              fontSize: "clamp(30px, 4vw, 48px)",
              fontWeight: 600,
              lineHeight: 1.08,
              letterSpacing: "-0.018em",
            }}
          >
            Messagerie sécurisée <span style={{ color: "#4FC3F7" }}>MSSanté</span> pour tous les professionnels de santé
          </h1>
          <p className="mt-5 max-w-md" style={{ color: "#93B4D4", fontSize: 15.5, lineHeight: 1.55 }}>
            Une <strong style={{ color: "#ffffff", fontWeight: 600 }}>boîte organisationnelle multi-utilisateurs</strong> pour
            tous les acteurs du parcours de soin — médicaux et non-médicaux. Conforme au référentiel ANS,
            hébergement HDS, <strong style={{ color: "#ffffff", fontWeight: 600 }}>interopérable</strong> avec vos logiciels métiers.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Link
              to="/mssante/inscription"
              data-testid="mssante-cta-register"
              className="inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
              style={{
                background: "#4FC3F7", color: "#0F2B5B",
                fontWeight: 500, fontSize: 14,
                borderRadius: 8, padding: "12px 22px", textDecoration: "none",
                boxShadow: "0 12px 28px rgba(79,195,247,0.32)",
              }}
            >
              <UserPlus size={15} /> Créer ma boîte MSSanté
            </Link>
            <Link
              to="/demo"
              data-testid="mssante-cta-demo"
              className="inline-flex items-center gap-2"
              style={{
                background: "transparent", color: "#B8D4E8",
                border: "0.5px solid rgba(184,212,232,0.30)",
                fontSize: 13.5, borderRadius: 8, padding: "11px 20px",
                textDecoration: "none",
              }}
            >
              <Calendar size={15} /> Demander une démo
            </Link>
          </div>
          <p className="mt-5 text-[12px]" style={{ color: "#93B4D4" }}>
            Sans configuration technique · Mise en service 24-48 h · Sans engagement
          </p>
        </div>

        {/* RIGHT — White with key benefits */}
        <div
          className="px-6 sm:px-10 lg:px-14 py-16 lg:py-20 flex flex-col justify-center"
          style={{ background: "#ffffff", borderLeft: "0.5px solid #E8EDF5" }}
        >
          <div className="flex items-center gap-2 mb-6">
            <span style={{ width: 3, height: 22, background: "#4FC3F7", borderRadius: 2 }} />
            <span style={{ fontSize: 11, color: "#4FC3F7", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
              Vos bénéfices clés
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {[
              { Icon: Building2, label: "Rattachée à votre structure (SIRET/FINESS), pas à une personne" },
              { Icon: Users, label: "Multi-utilisateurs : responsable + cotitulaires + délégataires" },
              { Icon: Lock, label: "Chiffrement S/MIME · hébergement HDS · journalisation PGSSI-S" },
              { Icon: Star, label: "Sous-domaines exclusifs : @opticiens.mssante.fr, @audio.mssante.fr…" },
              { Icon: Briefcase, label: "Conforme ANS et interopérable avec vos logiciels métiers" },
            ].map(({ Icon, label }, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3"
                style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD", borderRadius: 10 }}
              >
                <div className="grid place-items-center h-8 w-8 rounded-md shrink-0" style={{ background: "#ffffff", border: "0.5px solid #BAE6FD", color: "#0F2B5B" }}>
                  <Icon size={14} />
                </div>
                <span style={{ color: "#0F2B5B", fontSize: 13.5, lineHeight: 1.5, fontWeight: 500 }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProblemSection() {
  const problems = [
    "Messages perdus quand le titulaire est absent",
    "Délais de réponse aux prescripteurs qui s'allongent",
    "Manque de traçabilité sur les échanges",
    "Organisation fragile dépendant d'une seule personne",
  ];
  return (
    <Section variant="soft" eyebrow={{ icon: AlertTriangle, label: "Le constat", tone: "warn" }}
             title="Le vrai problème aujourd'hui">
      <div className="max-w-3xl mx-auto">
        <p className="text-[15px] text-[#64748B] leading-relaxed text-center">
          Dans la majorité des cabinets d'optique, des centres d'audioprothèse et des structures
          paramédicales, <strong className="text-[#0F2B5B]">un seul collaborateur gère les échanges MSSanté</strong>.
          Les messages arrivent sur une boîte personnelle, et personne d'autre ne peut intervenir.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {problems.map((p) => (
            <div key={p} className="flex items-start gap-3 rounded-lg border border-[rgba(255,180,80,0.20)] bg-[rgba(255,180,80,0.04)] p-4">
              <div className="grid place-items-center h-7 w-7 rounded-full bg-[rgba(255,180,80,0.12)] text-[#B45309] shrink-0 mt-0.5">
                <AlertTriangle size={13} />
              </div>
              <p className="text-[13px] text-[#0F2B5B] leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
        <div className="mt-7 rounded-xl border border-[rgba(255,120,120,0.25)] bg-[rgba(255,120,120,0.06)] p-5 flex items-start gap-3">
          <ArrowDownRight size={18} className="text-[#FFB4B4] shrink-0 mt-0.5" />
          <p className="text-[13.5px] text-[#0F2B5B]">
            <strong>Dès qu'il y a une absence, un congé ou un départ, tout le système se bloque.</strong>
          </p>
        </div>
      </div>
    </Section>
  );
}

function SolutionSection() {
  const features = [
    { icon: Building2, label: "Rattachée à votre structure (SIRET/FINESS), pas à une personne" },
    { icon: Users, label: "Accessible à plusieurs utilisateurs simultanément" },
    { icon: ShieldCheck, label: "Pilotée avec des droits granulaires (responsable, cotitulaire, délégataire)" },
  ];
  return (
    <Section variant="grid" eyebrow={{ icon: Sparkles, label: "La solution", tone: "mint" }}
             title="La boîte mail organisationnelle MSSanté">
      <div className="max-w-3xl mx-auto">
        <p className="text-[15px] text-[#64748B] leading-relaxed text-center mb-8">
          Une boîte organisationnelle, c'est une messagerie sécurisée MSSanté pensée pour fonctionner
          en équipe. <strong className="text-[#0F2B5B]">Votre organisation devient autonome</strong>, vos
          échanges avec les prescripteurs ne dépendent plus d'une seule personne.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.label} className="mc-card p-5">
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.12)] border border-[rgba(110,231,197,0.3)] text-[var(--mc-mint)] mb-3">
                <f.icon size={18} />
              </div>
              <p className="text-[13px] text-[#0F2B5B] leading-relaxed">{f.label}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function ComparisonSection() {
  return (
    <Section variant="soft" eyebrow={{ icon: Network, label: "Comparaison", tone: "blue" }}
             title="Boîte personnelle vs. boîte organisationnelle">
      <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
        <CompareCard
          Icon={User}
          tone="warn"
          title="Boîte personnelle"
          subtitle="jean.dupond@pro.mssante.fr"
          items={[
            "Liée à un professionnel",
            "Accès unique",
            "Dépend d'un individu",
          ]}
          verdict="Fragile · Non scalable"
        />
        <CompareCard
          Icon={Building2}
          tone="mint"
          title="Boîte organisationnelle"
          subtitle="cabinet-lefevre@opticiens.mssante.fr"
          items={[
            "Liée à votre structure",
            "Accès multi-utilisateurs",
            "Continuité assurée même en cas de turnover",
          ]}
          verdict="Robuste · Professionnelle · Durable"
          highlighted
        />
      </div>
    </Section>
  );
}

function CompareCard({ Icon, tone, title, subtitle, items, verdict, highlighted }) {
  const colors = tone === "mint"
    ? "border-[rgba(110,231,197,0.35)] bg-[rgba(110,231,197,0.06)]"
    : "border-[rgba(255,180,80,0.25)] bg-[rgba(255,180,80,0.04)]";
  const iconColors = tone === "mint" ? "text-[var(--mc-mint)] bg-[rgba(110,231,197,0.14)]" : "text-[#B45309] bg-[rgba(255,180,80,0.12)]";
  return (
    <div className={`mc-card p-6 lg:p-7 ${colors} ${highlighted ? "ring-1 ring-[var(--mc-mint)]" : ""}`}>
      <div className={`grid place-items-center h-11 w-11 rounded-full ${iconColors} mb-4`}>
        <Icon size={20} />
      </div>
      <h3 className="font-display text-[#0F2B5B] text-[20px]">{title}</h3>
      <code className="block mt-1 text-[12px] text-[#64748B] font-mono break-all">{subtitle}</code>
      <ul className="mt-4 grid gap-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-[13.5px] text-[#475569]">
            {tone === "mint" ? (
              <Check size={15} className="text-[var(--mc-mint)] mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle size={14} className="text-[#B45309] mt-0.5 shrink-0" />
            )}
            <span>{it}</span>
          </li>
        ))}
      </ul>
      <div className={`mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border ${
        tone === "mint"
          ? "border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] bg-[rgba(110,231,197,0.06)]"
          : "border-[rgba(255,180,80,0.4)] text-[#B45309] bg-[rgba(255,180,80,0.04)]"
      }`}>
        {tone === "mint" ? <Star size={12} /> : <AlertTriangle size={12} />} {verdict}
      </div>
    </div>
  );
}

function ContinuitySection() {
  return (
    <Section variant="grid" eyebrow={{ icon: RefreshCw, label: "Continuité", tone: "blue" }}
             title="Zéro rupture dans vos échanges">
      <div className="max-w-3xl mx-auto">
        <p className="text-[15px] text-[#64748B] leading-relaxed text-center mb-8">
          Avec MetaConnect, plusieurs personnes peuvent traiter les messages, les droits sont
          modifiables instantanément et <strong className="text-[#0F2B5B]">votre adresse reste la même
          dans le temps</strong>.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { icon: TrendingUp, label: "Turnover" },
            { icon: UserPlus, label: "Recrutement" },
            { icon: Calendar, label: "Congés" },
          ].map((c) => (
            <div key={c.label} className="rounded-lg border border-[rgba(127,179,255,0.18)] bg-[rgba(45,123,255,0.04)] p-4 flex items-center gap-3">
              <div className="grid place-items-center h-9 w-9 rounded-full bg-[rgba(45,123,255,0.18)] text-[#0284C7] shrink-0">
                <c.icon size={15} />
              </div>
              <span className="text-[13px] text-[#0F2B5B]">{c.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <span className="inline-flex items-center gap-2 text-[13px] rounded-full px-4 py-2 bg-[rgba(110,231,197,0.08)] border border-[rgba(110,231,197,0.25)] text-[var(--mc-mint)]">
            <Check size={14} /> Vos échanges continuent sans interruption
          </span>
        </div>
      </div>
    </Section>
  );
}

function BrandImageSection() {
  return (
    <Section variant="soft" eyebrow={{ icon: Briefcase, label: "Image professionnelle", tone: "mint" }}
             title="Une adresse qui reflète votre activité">
      <div className="max-w-2xl mx-auto">
        <div className="mc-card p-7 lg:p-8 border-[rgba(110,231,197,0.35)] bg-[rgba(110,231,197,0.05)]">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-mint)] mb-4 inline-flex items-center gap-2">
            <Building2 size={12} /> Boîte organisationnelle MetaConnect
          </div>
          <code className="block text-[15px] lg:text-[16px] font-mono text-[#0F2B5B] break-all px-4 py-3 rounded-lg bg-[#F0F9FF] border border-[rgba(110,231,197,0.20)]">
            nom-souhaité@opticiens.mssante.fr
          </code>
          <p className="mt-4 text-[13px] text-[#475569] leading-relaxed">
            MetaConnect est <strong className="text-[var(--mc-mint)]">propriétaire de sous-domaines
            MSSanté métier</strong>, accessibles à ses clients à titre exceptionnel — votre adresse
            reflète votre activité, rattachée à votre structure (SIRET / FINESS), partagée par
            l'équipe avec des droits différenciés.
          </p>
          <div className="mt-5 grid gap-1.5">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[var(--mc-mint)]/80">Sous-domaines disponibles</div>
            {["opticiens.mssante.fr", "audioprothesistes.mssante.fr", "optique.mssante.fr"].map((d) => (
              <code key={d} className="text-[12px] font-mono text-[#0F2B5B] px-2.5 py-1 rounded bg-[#F0F9FF] border border-[rgba(110,231,197,0.20)]">
                @{d}
              </code>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-8 max-w-2xl mx-auto">
        <p className="text-[13.5px] text-[#64748B] text-center mb-4">Pour les médecins prescripteurs :</p>
        <div className="grid sm:grid-cols-3 gap-3 text-center">
          {[
            { icon: FileCheck, label: "Identification claire" },
            { icon: HeartPulse, label: "Confiance renforcée" },
            { icon: Eye, label: "Meilleure lisibilité" },
          ].map((b) => (
            <div key={b.label} className="rounded-lg border border-[rgba(127,179,255,0.18)] bg-[rgba(45,123,255,0.04)] p-4">
              <b.icon size={16} className="mx-auto text-[#0284C7] mb-1.5" />
              <p className="text-[12.5px] text-[#475569]">{b.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* === Custom subdomain offer (chains, groups, national brands) === */}
      <div className="mt-10 max-w-3xl mx-auto">
        <div className="mc-card p-6 lg:p-7 border-[rgba(168,140,255,0.40)] bg-[rgba(168,140,255,0.05)]">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(168,140,255,0.18)] border border-[rgba(168,140,255,0.45)] text-[#d2c2ff] shrink-0">
              <Briefcase size={20} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#d2c2ff] mb-1">Enseignes · Groupes · Réseaux nationaux</div>
              <h3 className="font-display text-[#0F2B5B] text-[20px]">Votre propre sous-domaine MSSanté personnalisé</h3>
              <p className="mt-2 text-[13px] text-[#64748B] leading-relaxed">
                En tant qu'opérateur MSSanté agréé, MetaConnect peut <strong className="text-[#0F2B5B]">réserver un sous-domaine sur-mesure</strong> pour votre enseigne, votre groupe ou votre réseau. Idéal si vous voulez être identifié à l'échelle nationale par les prescripteurs.
              </p>
              <div className="mt-4 grid gap-2">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#64748B]">Exemple — votre adresse pourrait ressembler à :</div>
                <code className="text-[13px] font-mono text-[#0F2B5B] px-3 py-2 rounded-lg bg-[#F0F9FF] border border-[rgba(168,140,255,0.30)]">
                  paris@<span className="text-[#d2c2ff]">votre-enseigne</span>.mssante.fr
                </code>
                <code className="text-[13px] font-mono text-[#0F2B5B] px-3 py-2 rounded-lg bg-[#F0F9FF] border border-[rgba(168,140,255,0.30)]">
                  lyon@<span className="text-[#d2c2ff]">votre-enseigne</span>.mssante.fr
                </code>
              </div>
              <p className="mt-3 text-[12px] text-[#64748B] leading-relaxed">
                Étude personnalisée sous forme de devis (réservation auprès de l'ANS, paramétrage,
                déploiement multi-sites). Prévoir <strong className="text-[#0F2B5B]">2 à 4 semaines</strong> entre la commande et la mise en service.
              </p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <a href="mailto:contact@metaconnect.fr?subject=Demande%20de%20devis%20—%20Sous-domaine%20MSSant%C3%A9%20personnalis%C3%A9&body=Bonjour%2C%0A%0AJe%20repr%C3%A9sente%20l'enseigne%20%2F%20le%20groupe%20%3A%20%5BNom%5D%0ASIRET%20%3A%20%5BNum%C3%A9ro%5D%0ANombre%20de%20sites%20%3A%20%5BX%5D%0ASous-domaine%20souhait%C3%A9%20%3A%20%5BNom%5D.mssante.fr%0A%0APourriez-vous%20me%20faire%20parvenir%20un%20devis%20%3F%0A%0AMerci%2C"
                   data-testid="mssante-custom-subdomain-quote"
                   className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
                  <Briefcase size={13} /> Demander un devis personnalisé
                </a>
                <Link to="/demo" className="btn-ghost px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
                  <Calendar size={13} /> En parler avec un expert
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function ParamedicalSection() {
  const pros = [
    { icon: Eye, label: "Opticiens-lunetiers", desc: "Échanges avec ophtalmologistes, orthoptistes, prescripteurs" },
    { icon: Ear, label: "Audioprothésistes", desc: "Coordination avec ORL, médecins traitants, orthophonistes" },
    { icon: Stethoscope, label: "Médecins (généralistes & spécialistes)", desc: "Ophtalmologistes, ORL, gériatres, médecins traitants" },
    { icon: MessageCircle, label: "Orthophonistes & orthoptistes", desc: "Suivi pluridisciplinaire en équipe coordonnée" },
    { icon: HeartPulse, label: "Centres de santé & MSP", desc: "Maisons de santé pluriprofessionnelles, multi-praticiens" },
    { icon: Users, label: "Structures médico-sociales", desc: "EHPAD, IME, SESSAD, FAM, MAS — équipes pluridisciplinaires" },
  ];
  return (
    <Section variant="grid" eyebrow={{ icon: Stethoscope, label: "Pour qui ?", tone: "mint" }}
             title="Tous les professionnels de santé, médicaux et non-médicaux">
      <div className="max-w-3xl mx-auto mb-8">
        <p className="text-[15px] text-[#64748B] leading-relaxed text-center">
          La MSSanté concerne <strong className="text-[#0F2B5B]">tout l'écosystème de santé</strong> :
          médecins, paramédicaux, opticiens, audioprothésistes, structures médico-sociales… Toute
          profession qui doit échanger des données de santé de manière sécurisée et garantir un
          suivi fiable.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
        {pros.map((p) => (
          <div key={p.label} className="mc-card p-5">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0284C7] mb-3">
              <p.icon size={18} />
            </div>
            <h3 className="font-display text-[#0F2B5B] text-[15px]">{p.label}</h3>
            <p className="mt-1.5 text-[12.5px] text-[#64748B] leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function OhMyVueIntegrationSection() {
  return (
    <Section variant="soft" eyebrow={{ icon: Network, label: "Écosystème MetaConnect", tone: "mint" }}
             title="MSSanté + OhMyVue + OhMyOuie — l'écosystème de coordination du parcours de soin">
      <div className="max-w-4xl mx-auto">
        <p className="text-[15px] text-[#64748B] leading-relaxed text-center mb-8">
          MetaConnect ne se limite pas à la messagerie sécurisée. Combinée à nos plateformes métier
          <strong className="text-[#0F2B5B]"> <a href="https://ohmyvue.fr" target="_blank" rel="noopener noreferrer" className="text-[var(--mc-mint)] hover:underline">OhMyVue.fr</a> </strong>
          (visuel) et <strong className="text-[#0F2B5B]">OhMyOuie</strong> (auditif), MSSanté devient un
          véritable <strong className="text-[#0F2B5B]">moteur d'information automatique</strong> entre
          opticiens, ophtalmologistes, audioprothésistes, ORL et médecins traitants.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="mc-card p-5 border-[rgba(110,231,197,0.30)] bg-[rgba(110,231,197,0.04)]">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.15)] border border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] mb-3">
              <Eye size={18} />
            </div>
            <div className="font-display text-[#0F2B5B] text-[15px] mb-1">OhMyVue.fr</div>
            <p className="text-[12px] text-[#64748B] leading-relaxed">
              Plateforme du parcours visuel : opticien ↔ ophtalmologiste ↔ orthoptiste, partage de
              prescriptions et suivi des renouvellements.
            </p>
          </div>
          <div className="mc-card p-5 border-[rgba(168,140,255,0.30)] bg-[rgba(168,140,255,0.04)]">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(168,140,255,0.15)] border border-[rgba(168,140,255,0.4)] text-[#d2c2ff] mb-3">
              <Ear size={18} />
            </div>
            <div className="font-display text-[#0F2B5B] text-[15px] mb-1">OhMyOuie</div>
            <p className="text-[12px] text-[#64748B] leading-relaxed">
              Plateforme du parcours auditif : audioprothésiste ↔ ORL ↔ médecin traitant,
              partage des bilans et suivi de l'appareillage.
            </p>
          </div>
          <div className="mc-card p-5 border-[rgba(127,179,255,0.30)] bg-[rgba(45,123,255,0.04)]">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0284C7] mb-3">
              <ShieldCheck size={18} />
            </div>
            <div className="font-display text-[#0F2B5B] text-[15px] mb-1">MSSanté MetaConnect</div>
            <p className="text-[12px] text-[#64748B] leading-relaxed">
              Le canal sécurisé qui transporte les informations entre tous les acteurs : conforme
              ANS, S/MIME, traçable.
            </p>
          </div>
        </div>

        <div className="mc-card p-6 lg:p-7">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-mint)] mb-3 inline-flex items-center gap-2">
            <Sparkles size={12} /> Devoir d'information automatique
          </div>
          <p className="text-[14px] text-[#0F2B5B] leading-relaxed mb-4">
            Quand un opticien adapte un équipement, l'ophtalmologiste reçoit l'information.
            Quand un audioprothésiste démarre un appareillage, l'ORL et le médecin traitant
            sont informés. <strong className="text-[var(--mc-mint)]">Plus de ressaisie, plus
            d'oublis</strong> — le devoir d'information entre professionnels est respecté
            automatiquement, et chaque acteur gagne du temps.
          </p>
          <ul className="grid sm:grid-cols-2 gap-2.5 mb-5">
            {[
              "Information automatique des prescripteurs lors des actes",
              "Traçabilité complète pour la coordination des soins",
              "Interopérable avec vos logiciels métier (Maincare, etc.)",
              "Conforme au cadre réglementaire et au secret médical",
              "Gain de temps administratif pour toutes les équipes",
              "Meilleure expérience patient grâce au suivi coordonné",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-[13px] text-[#475569]">
                <Check size={14} className="text-[var(--mc-mint)] mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="https://ohmyvue.fr" target="_blank" rel="noopener noreferrer"
               data-testid="mssante-link-ohmyvue"
               className="btn-ghost px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <Eye size={13} /> Découvrir OhMyVue.fr
            </a>
            <Link to="/demo" data-testid="mssante-cta-ecosystem"
                  className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <Phone size={13} /> Pour en savoir plus, contactez-nous
            </Link>
          </div>
        </div>
      </div>
    </Section>
  );
}

function SisterPagesSection() {
  const pages = [
    { to: "/mssante-medecin", label: "MSSanté Médecin", desc: "Cabinet, CPTS, MSP — coordination ville-hôpital", icon: Stethoscope },
    { to: "/mssante-infirmier-liberal", label: "MSSanté Infirmier libéral", desc: "IDEL en tournée, DMP mobile", icon: UserRound },
    { to: "/mssante/opticien", label: "MSSanté Opticien", desc: "Tout le détail pour opticiens-lunetiers", icon: Eye },
    { to: "/mssante/audioprothesiste", label: "MSSanté Audioprothésiste", desc: "Spécifique au parcours auditif", icon: Ear },
    { to: "/mssante/professionnels-de-sante", label: "Tous les professionnels", desc: "Panorama : médecins, paramédicaux, structures", icon: HeartPulse },
    { to: "/mssante/inscription", label: "Créer ma BAL MSSanté", desc: "Souscription en ligne — 24-48 h", icon: MailPlus },
    { to: "/lutte-fraude-sante", label: "Lutte fraude santé", desc: "Détection documentaire pour assureurs", icon: ShieldAlert },
    { to: "/mssante-assureurs", label: "MSSanté Assureurs", desc: "Échanges sécurisés assureurs / professionnels", icon: BriefcaseIcon },
  ];
  return (
    <Section variant="grid" eyebrow={{ icon: Network, label: "Approfondir par profession", tone: "blue" }}
             title="Pages dédiées par métier">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
        {pages.map((p) => (
          <Link key={p.to} to={p.to} data-testid={`mssante-sister-${p.to.replace(/\W+/g, "-")}`}
                className="mc-card p-5 hover:border-[var(--mc-blue-400)] transition group">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0284C7] mb-3">
              <p.icon size={18} />
            </div>
            <h3 className="font-display text-[#0F2B5B] text-[15px] inline-flex items-center gap-1.5">
              {p.label}
              <ArrowRight size={14} className="text-[#0284C7] group-hover:translate-x-0.5 transition" />
            </h3>
            <p className="mt-1.5 text-[12.5px] text-[#64748B] leading-relaxed">{p.desc}</p>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function NationalAlignmentSection() {
  return (
    <Section variant="soft" eyebrow={{ icon: Network, label: "Cadre national", tone: "blue" }}
             title="Aligné avec la stratégie nationale e-santé">
      <div className="max-w-3xl mx-auto">
        <p className="text-[15px] text-[#64748B] leading-relaxed text-center mb-8">
          La MSSanté s'inscrit dans le <strong className="text-[#0F2B5B]">Ségur du numérique en santé</strong>,
          l'annuaire santé national et les échanges sécurisés entre professionnels.
        </p>
        <ul className="grid gap-3 max-w-xl mx-auto">
          {[
            "Référentiel MSSanté de l'Agence du Numérique en Santé (ANS)",
            "Hébergement Données de Santé (HDS) certifié",
            "Annuaire santé national — visibilité auprès des prescripteurs",
            "Conformité RGPD et secret médical (PSSI-S)",
          ].map((it) => (
            <li key={it} className="flex items-start gap-2.5 text-[14px] text-[#0F2B5B]">
              <Check size={16} className="text-[var(--mc-mint)] mt-0.5 shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

function PlatformSection() {
  const cards = [
    {
      icon: MailPlus, title: "Créez votre boîte en quelques minutes",
      points: ["Nom personnalisé", "Domaine MSSanté au choix", "Structure intégrée"],
    },
    {
      icon: Users, title: "Gérez vos utilisateurs simplement",
      points: ["Responsable principal", "Cotitulaires (pleine utilisation)", "Délégataires (lecture/délégation)"],
    },
    {
      icon: Lock, title: "Sécurisez tous vos échanges",
      points: ["Conformité MSSanté ANS", "Traçabilité native", "Confidentialité S/MIME"],
    },
    {
      icon: Sparkles, title: "Structurez votre organisation",
      points: ["Centralisation des messages", "Gestion des accès en temps réel", "Continuité garantie"],
    },
  ];
  return (
    <Section variant="grid" eyebrow={{ icon: Rocket, label: "MetaConnect (AMSS)", tone: "mint" }}
             title="Simplicité + puissance">
      <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
        {cards.map((c) => (
          <div key={c.title} className="mc-card p-6">
            <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(110,231,197,0.12)] border border-[rgba(110,231,197,0.3)] text-[var(--mc-mint)] mb-4">
              <c.icon size={20} />
            </div>
            <h3 className="font-display text-[#0F2B5B] text-[17px] mb-3">{c.title}</h3>
            <ul className="grid gap-1.5">
              {c.points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-[13px] text-[#475569]">
                  <Check size={14} className="text-[var(--mc-mint)] mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

function BenefitsSection() {
  const items = [
    "Moins de pertes d'informations",
    "Des échanges plus rapides avec les prescripteurs",
    "Une organisation plus fiable et professionnelle",
    "Une meilleure image auprès des médecins et patients",
    "Une conformité ANS / RGPD renforcée",
  ];
  return (
    <Section variant="soft" eyebrow={{ icon: TrendingUp, label: "Bénéfices", tone: "mint" }}
             title="Ce que vous gagnez concrètement">
      <ul className="grid gap-3 max-w-2xl mx-auto">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-3 rounded-lg border border-[rgba(110,231,197,0.20)] bg-[rgba(110,231,197,0.04)] p-4">
            <div className="grid place-items-center h-7 w-7 rounded-full bg-[rgba(110,231,197,0.14)] text-[var(--mc-mint)] shrink-0 mt-0.5">
              <Check size={14} />
            </div>
            <p className="text-[14px] text-[#0F2B5B] leading-relaxed">{it}</p>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function FAQSection() {
  return (
    <Section variant="grid" eyebrow={{ icon: MessageCircle, label: "FAQ", tone: "blue" }}
             title="Questions fréquentes sur la MSSanté">
      <div className="max-w-3xl mx-auto grid gap-3">
        {FAQ.map((f, i) => (
          <details key={f.q} className="mc-card p-5 group" data-testid={`mssante-faq-${i}`}>
            <summary className="cursor-pointer list-none flex items-start justify-between gap-3 text-[14.5px] text-[#0F2B5B] font-medium leading-snug">
              <span>{f.q}</span>
              <ArrowRight size={16} className="shrink-0 mt-0.5 text-[#0284C7] transition group-open:rotate-90" />
            </summary>
            <p className="mt-3 text-[13.5px] text-[#64748B] leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

function FinalCTA() {
  return (
    <section className="hex-grid py-16 lg:py-24" aria-labelledby="cta-h2">
      <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)] mb-4">
          <ShieldCheck size={13} /> Passez à l'action
        </div>
        <h2 id="cta-h2" className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
          Passez à une messagerie <span className="text-[var(--mc-mint)]">adaptée à votre structure</span>
        </h2>
        <p className="mt-4 text-[15px] text-[#64748B] max-w-xl mx-auto leading-relaxed">
          Ne laissez plus votre organisation dépendre d'une seule personne. Gagnez en efficacité,
          en sécurité et en crédibilité.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/mssante/inscription" data-testid="mssante-final-cta-create"
                className="btn-primary px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
            <MailCheck size={15} /> Créer ma boîte MSSanté maintenant
          </Link>
          <Link to="/demo" data-testid="mssante-final-cta-demo"
                className="btn-ghost px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
            <Calendar size={15} /> Demander une démo
          </Link>
          <a href="mailto:contact@metaconnect.fr?subject=Rappel%20expert%20MSSanté"
             data-testid="mssante-final-cta-callback"
             className="btn-ghost px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
            <Phone size={15} /> Être rappelé par un expert
          </a>
        </div>
        <p className="mt-6 text-[12px] text-[#64748B]">
          Sans engagement · Tarification transparente · Mise en service sous 24-48 h
        </p>
      </div>
    </section>
  );
}

// ============================================================
// Atoms
// ============================================================

function Section({ variant = "grid", eyebrow, title, children }) {
  const Icon = eyebrow?.icon;
  const toneClass = eyebrow?.tone === "mint"
    ? "text-[var(--mc-mint)]"
    : eyebrow?.tone === "warn"
      ? "text-[#B45309]"
      : "text-[#0284C7]";
  const bgClass = variant === "soft" ? "hex-soft" : "hex-grid";
  return (
    <section className={`${bgClass} py-14 lg:py-20`}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          {eyebrow && (
            <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] ${toneClass} mb-3`}>
              {Icon && <Icon size={13} />} {eyebrow.label}
            </div>
          )}
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            {title}
          </h2>
        </div>
        {children}
      </div>
    </section>
  );
}
